import type { SegmentRule, SegmentRulesPayload } from '@/api/marketing.api';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

const FIELDS = [
  { value: 'LEAD_SCORE', label: 'Lead score' },
  { value: 'PURCHASE_HISTORY', label: 'Purchase history (POS)' },
  { value: 'REVENUE', label: 'Expected revenue' },
  { value: 'LOCATION', label: 'Location' },
  { value: 'EMAIL_ACTIVITY', label: 'Email activity' },
  { value: 'CAMPAIGN_ACTIVITY', label: 'Campaign activity' },
  { value: 'WEBSITE_ACTIVITY', label: 'Website activity' },
];

const OPERATORS = [
  { value: 'gte', label: '>=' },
  { value: 'gt', label: '>' },
  { value: 'lte', label: '<=' },
  { value: 'lt', label: '<' },
  { value: 'eq', label: '=' },
  { value: 'contains', label: 'contains' },
];

type Props = {
  rules: SegmentRulesPayload;
  onChange: (rules: SegmentRulesPayload) => void;
};

export function SegmentRuleBuilder({ rules, onChange }: Props) {
  const updateGroup = (index: number, patch: Partial<SegmentRule>) => {
    const groups = [...rules.groups];
    groups[index] = { ...groups[index], ...patch };
    onChange({ ...rules, groups });
  };

  const addRule = () => {
    onChange({
      ...rules,
      groups: [...rules.groups, { field: 'LEAD_SCORE', operator: 'gte', value: 10 }],
    });
  };

  const removeRule = (index: number) => {
    onChange({ ...rules, groups: rules.groups.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <Select
        label="Match mode"
        options={[
          { value: 'OR', label: 'Any rule (OR)' },
          { value: 'AND', label: 'All rules (AND)' },
        ]}
        value={rules.matchMode}
        onChange={(v) => onChange({ ...rules, matchMode: v as 'AND' | 'OR' })}
      />
      {rules.groups.map((rule, idx) => (
        <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end p-3 bg-slate-50 rounded-lg border border-slate-100">
          <Select
            label="Field"
            options={FIELDS}
            value={rule.field}
            onChange={(v) => updateGroup(idx, { field: v })}
          />
          <Select
            label="Operator"
            options={OPERATORS}
            value={rule.operator}
            onChange={(v) => updateGroup(idx, { operator: v })}
          />
          <div className="md:col-span-2">
            <Input
              label="Value"
              value={String(rule.value)}
              onChange={(e) => updateGroup(idx, { value: e.target.value })}
            />
          </div>
          <button type="button" className="text-xs text-red-600 pb-1" onClick={() => removeRule(idx)}>Remove</button>
        </div>
      ))}
      <button type="button" className="text-sm text-violet-600 font-medium" onClick={addRule}>+ Add rule group</button>
    </div>
  );
}

export const defaultSegmentRules: SegmentRulesPayload = {
  matchMode: 'OR',
  groups: [{ field: 'LEAD_SCORE', operator: 'gte', value: 10 }],
};
